import { useMemo } from 'react';
import * as React from 'react';

import requestCountry from 'js/lib/requestCountry';

import { useGenerateStartFreeTrialTestEpicImpression } from 'bundles/coursera-plus/components/elevateLTV/startFreeTrial/useGenerateStartFreeTrialTestEpicImpression';
import RoleDescriptionJobTitles from 'bundles/unified-career-academy/components/modules/description/RoleDescriptionJobTitles';
import RoleDescriptionModule from 'bundles/unified-career-academy/components/modules/description/RoleDescriptionModule';
import RoleDescriptionSkills from 'bundles/unified-career-academy/components/modules/description/RoleDescriptionSkills';
import RolePageHeroBreadcrums from 'bundles/unified-career-academy/components/modules/hero/RolePageHeroBreadcrumbs';
import RolePageHeroCareerStats from 'bundles/unified-career-academy/components/modules/hero/RolePageHeroCareerStats';
import RolePageHeroModule from 'bundles/unified-career-academy/components/modules/hero/RolePageHeroModule';
import RolePageHeroValueProps from 'bundles/unified-career-academy/components/modules/hero/RolePageHeroValueProps';
import { RolePageCareerResourceCard } from 'bundles/unified-career-academy/components/modules/rdp/RolePageCareerResourceCard';
import RolePageCareerResourcesModule from 'bundles/unified-career-academy/components/modules/rdp/RolePageCareerResourcesModule';
import { RolePageConsumerCertificateCard } from 'bundles/unified-career-academy/components/modules/rdp/RolePageConsumerCertificateCard';
import RolePageConsumerCertificatesModule from 'bundles/unified-career-academy/components/modules/rdp/RolePageConsumerCertificatesModule';
import RolePageShowcaseModule from 'bundles/unified-career-academy/components/modules/rdp/RolePageShowcaseModule';
import { useCareerAcademyVariant } from 'bundles/unified-career-academy/contexts/CareerAcademyVariantContext';
import { useRolePageContent } from 'bundles/unified-career-academy/contexts/RolePageContentContext';
import getCareerStats from 'bundles/unified-career-academy/utils/getCareerStats';
import { formatS12nId, getFilteredCertificates } from 'bundles/unified-career-academy/utils/helpers';

import _t from 'i18n!nls/career-academy';

const ConsumerRolePageRootContent = () => {
  const pageContent = useRolePageContent();
  const careerAcademyVariant = useCareerAcademyVariant();
  const careerStats = useMemo(
    () => getCareerStats(pageContent?.career as Record<string, string>),
    [pageContent?.career]
  );
  const showCareerStats = requestCountry.get() === 'US' && careerStats.length > 0;

  useGenerateStartFreeTrialTestEpicImpression({});

  if (!pageContent?.career || !pageContent?.template) {
    return null;
  }

  const {
    title,
    rdpDescriptionTitle,
    rdpDescriptionText,
    rdpDescriptionFootnotes,
    rdpDescriptionSkills,
    rdpDescriptionJobTitles,
    rdpHeroBackgroundImage,
    rdpHeroSubtitle,
    rdpHeroValueProps,
  } = pageContent.career;

  const { certificatesTitle, certificatesSubtitle } = pageContent.template;
  const careerResourcesTitle = _t('Explore career resources');
  const careerResourcesSubtitle = _t('Figure out if this career is right for you.');

  const filteredCertificates = getFilteredCertificates({
    certificates: pageContent?.career.programsCollection?.items,
    careerAcademyVariant,
  }).filter((certificate) => certificate?.programId);
  const certificates = filteredCertificates.length
    ? filteredCertificates.map((certificate, index) => (
        <RolePageConsumerCertificateCard
          productId={formatS12nId(certificate?.programId || '', true) ?? ''}
          index={index}
        />
      ))
    : null;

  const articles = [pageContent.career.beginnerArticle, pageContent.career.advancedArticle].filter(Boolean);
  const resources = articles.length
    ? articles.map(
        (article) =>
          article?.slug && <RolePageCareerResourceCard article={article} slug={article.slug} key={article.slug} />
      )
    : null;

  return (
    <section>
      <RolePageHeroModule
        breadcrumbs={<RolePageHeroBreadcrums roleTitle={title} backButtonUrl="/career-academy" />}
        title={title}
        subtitle={rdpHeroSubtitle}
        backgroundImage={rdpHeroBackgroundImage?.url}
        valueProps={<RolePageHeroValueProps valueProps={rdpHeroValueProps} />}
        careerStats={showCareerStats ? <RolePageHeroCareerStats careerStats={careerStats} /> : undefined}
      />
      <RoleDescriptionModule
        title={rdpDescriptionTitle}
        description={rdpDescriptionText}
        addExtraPadding={showCareerStats}
        footnotes={showCareerStats ? rdpDescriptionFootnotes : undefined}
        skills={<RoleDescriptionSkills skills={rdpDescriptionSkills} roleTitle={title} />}
        jobTitles={<RoleDescriptionJobTitles jobTitles={rdpDescriptionJobTitles} />}
      />
      <RolePageConsumerCertificatesModule
        title={certificatesTitle}
        subtitle={certificatesSubtitle}
        certificates={certificates}
      />
      <RolePageCareerResourcesModule
        title={careerResourcesTitle}
        subtitle={careerResourcesSubtitle}
        resources={resources}
      />
      <RolePageShowcaseModule />
    </section>
  );
};

export default ConsumerRolePageRootContent;
